<template>
  <o-panel-link
    v-if="reservation"
    :path="`/appointment/${data.idReservation}?date=${data.date}`"
  >
    <div class="w-full relative flex items-center justify-between">
      <div class="flex items-center">
        <div class="flex-shrink-0 font-medium mr-3 md:mr-4 text-center w-8 md:w-10">
          <p class="text-xs opacity-70">
            {{ dayLabel }}
          </p>
          <p class="md:text-lg">
            {{ dateLabel }}
          </p>
          <p class="text-xs opacity-70">
            {{ monthLabel }}
          </p>
        </div>
        <div
          class="flex flex-col self-stretch w-2 mr-4 rounded overflow-hidden divide-y-1 divide-white"
        >
          <div
            v-for="service in data.services"
            :key="`${data.idReservation}-${service.idReservationService}`"
            class="w-full flex-grow"
            :style="{
              backgroundColor: '#' + service.service.borderHexColour
            }"
          />
        </div>
        <div class="w-full py-1">
          <p class="text-sm">
            {{ startLabel }} - {{ finishLabel }}
          </p>
          <p class="text-base font-semibold leading-tight md:leading-loose my-1 md:my-0">
            {{ servicesLabel }}
          </p>
          <div class="text-sm opacity-70 flex flex-wrap leading-none">
            <span class="flex items-center mr-3 mb-1 leading-tight">
              <o-avatar :items="employees" class="hidden md:flex pr-2" />
              <span>{{ employeesLabel }}</span>
            </span>
            <div class="flex items-center">
              <span class="flex items-center mr-3 mb-1 leading-tight">
                <o-icon class="hidden md:block mr-2" icon="menuBookings" />
                <span>{{ $t('datetime.increments.mins', { mins: durationLabel }, Number(durationLabel)) }}</span>
              </span>
              <span class="flex items-center mr-3 mb-1 leading-tight">
                <o-icon class="hidden md:block mr-2" icon="price" />
                <span>{{ priceLabel }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-wrap justify-end items-center">
        <o-chip v-if="isCustomerConfirmed" flat variant="success" class="mr-1 mb-1">
          {{ $t('reservation.attending') }}
        </o-chip>
        <o-chip v-if="isStatusConfirmed" flat variant="success" class="hidden mr-1 mb-1">
          {{ $t('reservation.confirmed') }}
        </o-chip>
        <o-chip v-if="isStatusUnconfirmed" flat variant="warning" class="mr-1 mb-1">
          {{ $t('reservation.unconfirmed') }}
        </o-chip>
        <o-chip v-if="isStatusCancelled" flat class="mb-1 mr-1" variant="danger">
          {{ $t('general.cancelled') }}

          <span v-if="reservation.cancellationType">
            : {{ reservation.cancellationType.shortName }}
          </span>

          <span v-if="reservation.cancellationNote" class="ml-1">
            ({{ reservation.cancellationNote }})
          </span>
        </o-chip>
        <o-chip v-if="isPaid" flat variant="success" class="mr-1 mb-1">
          {{ $t('general.paid') }}
        </o-chip>
        <o-chip v-if="isCheckedIn" flat variant="info" class="mr-1 mb-1">
          {{ $t('reservation.checkedIn') }}
        </o-chip>
        <o-chip v-if="isNoShow" flat variant="warning" class="mr-1 mb-1">
          {{ $t('reservation.noShow') }}
        </o-chip>
        <o-chip v-if="hasPass" flat variant="success" class="mr-1 mb-1">
          {{ $t('general.pass') }}
        </o-chip>
        <o-chip v-if="isRecurring" flat variant="info" class="mr-1 mb-1">
          {{ $t('reservation.recurring') }}
        </o-chip>
        <o-chip v-if="isCourse" flat variant="info" class="mr-1 mb-1">
          Course
        </o-chip>
      </div>
    </div>
  </o-panel-link>
</template>

<script setup lang="ts">
import { OAvatar } from '@/components/elements'

interface Props {
  data: Record<string, any>
}

const props = defineProps<Props>()

const { $date } = useNuxtApp()

const reservation = computed(() => isGroup.value ? props.data.reservationCustomer : props.data)

const employees = computed(() => {
  return props.data.services
    .map(({ employee }: any) => employee)
})

const servicesLabel = computed(() => {
  return props.data.services
    .map(({ service }: any) => service.title)
    .join(', ')
})

const employeesLabel = computed(() => {
  const emps = props.data.services
    .map(({ employee }: any) => {
      return employee.displayName
    })

  return Array.from(new Set(emps))
    .join(', ')
})

const dayLabel = computed(() => {
  const date = props.data.date.value || props.data.date
  return ($date as any).convert(date, undefined, 'iii')
})

const dateLabel = computed(() => {
  const date = props.data.date.value || props.data.date
  return ($date as any).convert(date, undefined, 'do')
})

const monthLabel = computed(() => {
  const date = props.data.date.value || props.data.date
  return ($date as any).convert(date, undefined, 'MMM')
})

const startLabel = computed(() => props.data.startLabel)
const finishLabel = computed(() => props.data.finishLabel)

const durationLabel = computed(() => {
  return props.data.services
    .map((service: any) => service.duration)
    .reduce((a: number, b: number) => a + b, 0)
})

const priceLabel = computed(() => reservation.value?.priceLabel)
const isGroup = computed(() => props.data.isGroup)
const isPaid = computed(() => reservation.value.isPaid)
const isCheckedIn = computed(() => reservation.value.checkedIn)
const isNoShow = computed(() => reservation.value.noShow)
const isStatusConfirmed = computed(() => reservation.value.isStatusConfirmed)
const isStatusUnconfirmed = computed(() => reservation.value.isStatusUnconfirmed)
const isStatusCancelled = computed(() => reservation.value.isStatusCancelled)
const isCustomerConfirmed = computed(() => reservation.value.customerConfirmed)
const hasPass = computed(() => reservation.value.hasPass)
const isRecurring = computed(() => reservation.value.isRecurring)
const isCourse = computed(() => reservation.value.courseRunReservations?.length > 0)
</script>
